/**
 * ProjectList — projectenoverzicht.
 *
 * Standaard een rustige tabel, gesorteerd op risico: per project één signaal
 * (rood = te laat, oranje = risico; gezonde projecten blijven stil — regel in
 * list/projectGezondheid.js), fase, PL, een gedeelde tijdas, de volgende
 * deadline en uren-%. Taken, urendetail en productie-stoplicht via "Meer kolommen".
 * Eén bereik-keuze (mij / iedereen / een PL) en één statusfilter.
 */
import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Box, Typography, TextField, InputAdornment, Skeleton, Tooltip, Dialog, FormControl, Select, MenuItem, ListSubheader, Autocomplete, createFilterOptions } from '@mui/material';
import { fetchProjectStatussen } from '../../ui-components/ProjectStoplicht';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DeadlineOverview from './DeadlineOverview';
import { useMsal } from '@azure/msal-react';
import { BACKEND_URL, AI_ENGINE_URL } from '../../utils/apiConfig';
import { toast } from 'react-hot-toast';
import { authFetch } from '../../utils/authHelpers';
import PageHeader from '../../ui-components/PageHeader';
import ProjectTabel from './list/ProjectTabel';
import ProjectKaart from './list/ProjectKaart';
import { projectHref, opGewoneKlik } from './list/onderdelen';
import useProjectGezondheid from './list/useProjectGezondheid';
import {
  berekenGezondheid, grippVoorProject, vergelijkRisico, klantVanProject, vandaagDag,
  LEAD_STATUSSEN, AFGESLOTEN_STATUSSEN,
} from './list/projectGezondheid';

const T = {
  primary: '#526075', primaryDim: '#465469', primaryContainer: '#d5e3fd',
  tertiary: '#635983', tertiaryContainer: '#dbcdff',
  surface: '#ffffff', surfaceLow: '#f1f4f5', surfaceHigh: '#e5e9eb',
  text: '#2d3335', secondary: '#556071', outline: '#767c7e', outlineVariant: '#adb3b5',
  emerald: '#10b981', emeraldBg: '#ecfdf5',
  error: '#a83836', errorBg: '#fef2f2',
  font: '"Manrope", system-ui, sans-serif',
  fontBody: '"Inter", system-ui, sans-serif',
  shadow: '0 1px 3px rgba(45,51,53,0.04)',
};

const Icon = ({ name, size = 24, sx = {} }) => (
  <span className="material-symbols-outlined" style={{ fontSize: size, fontVariationSettings: "'FILL' 0, 'wght' 400", ...sx }}>{name}</span>
);

const statusColorMap = {
  'Lead': { bg: '#f3e8ff', text: '#635983' }, 'Offerte': { bg: '#dbeafe', text: '#2563eb' },
  'In progress': { bg: T.primaryContainer, text: T.primaryDim }, 'Afgerond': { bg: '#dcfce7', text: '#166534' },
  'On Hold': { bg: '#fef3c7', text: '#b45309' }, 'Gearchiveerd': { bg: T.surfaceLow, text: T.outline },
  'Geannuleerd': { bg: '#fee2e2', text: '#dc2626' },
};

const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '0.5rem', fontSize: '0.84rem', bgcolor: T.surfaceLow, fontFamily: T.fontBody,
    '& fieldset': { borderColor: 'rgba(45,51,53,0.08)' },
    '&.Mui-focused fieldset': { borderColor: T.primary },
  },
  '& .MuiInputLabel-root': { fontFamily: T.fontBody, fontSize: '0.82rem' },
};

// Lookup-velden kunnen array of string zijn — altijd normaliseren.
const alsLijst = (v) => (Array.isArray(v) ? v : v ? [v] : []).filter(Boolean).map(String);
const plVan = (p) => alsLijst(p.fields?.['Projectleider (Volledige naam)'] || p.fields?.['Projectleider (Voornaam)']);
const isLeadStatus = (p) => LEAD_STATUSSEN.includes(p.fields?.Status);
const isAfgesloten = (p) => AFGESLOTEN_STATUSSEN.includes(p.fields?.Status);
const isLopend = (p) => !isLeadStatus(p) && !isAfgesloten(p);

const matchtZoek = (p, s) => {
  if (!s) return true;
  const name = (p.fields?.Projectnr_en_naam || p.fields?.Projectnaam || '').toLowerCase();
  const num = (p.fields?.Projectnummer || '').toString().toLowerCase();
  const cn = p.fields?.KlantnaamString || p.fields?.Klantnaam || [];
  const cl = (Array.isArray(cn) ? (cn[0] || '') : String(cn || '')).toLowerCase();
  // Projecttype is een Airtable multipleSelects en kan dus een array zijn.
  const tpRaw = p.fields?.Projecttype || '';
  const tp = (Array.isArray(tpRaw) ? tpRaw.join(', ') : String(tpRaw)).toLowerCase();
  return name.includes(s) || num.includes(s) || cl.includes(s) || tp.includes(s);
};

// Bereik: één keuze voor "van wie" (vervangt Mijn/Alle projecten + PL-filter).
const BEREIK_MIJ = '__mij__';
const BEREIK_ALLE = '__alle__';

const STATUS_OPTIES = [
  { key: 'lopend', label: 'Lopend' },
  { key: 'leads', label: 'Offertes & leads' },
  { key: 'archief', label: 'Archief' },
  { key: 'alles', label: 'Alles' },
];

const SORTERINGEN = [
  ['risico', 'Risico (hoogste eerst)', 'Risico'],
  ['deadline', 'Eerstvolgende deadline', 'Deadline'],
  ['uren', 'Urenverbruik', 'Uren'],
  ['nummer', 'Projectnummer (nieuw eerst)', 'Nummer'],
  ['naam', 'Naam', 'Naam'],
  ['pl', 'Projectleider', 'PL'],
];

const leesWeergave = () => {
  try {
    const w = localStorage.getItem('projectenWeergave.v2');
    return w === 'kaarten' ? 'kaarten' : 'tabel';
  } catch { return 'tabel'; }
};

const leesDetails = () => {
  try { return localStorage.getItem('projectenDetails.v1') === '1'; } catch { return false; }
};

// Filters onthouden als je een project opent en terugkomt (wiki 26 sep): per
// tabblad (sessionStorage), zodat een nieuw tabblad weer schoon begint.
const FILTER_SLEUTEL = 'projectenFilters.v1';
const leesFilters = () => {
  try {
    const f = JSON.parse(sessionStorage.getItem(FILTER_SLEUTEL) || '{}') || {};
    return {
      bereik: typeof f.bereik === 'string' && f.bereik ? f.bereik : BEREIK_MIJ,
      status: STATUS_OPTIES.some((o) => o.key === f.status) ? f.status : 'lopend',
      sortering: SORTERINGEN.some(([k]) => k === f.sortering) ? f.sortering : 'risico',
      zoek: typeof f.zoek === 'string' ? f.zoek : '',
    };
  } catch { return { bereik: BEREIK_MIJ, status: 'lopend', sortering: 'risico', zoek: '' }; }
};

// Klantfilter (?klant=<naam>, vanuit "Gekoppelde bedrijven" op een account): exact op de
// klantnaam, over álle gekoppelde klanten van een project en alle projectleiders — een
// zoekterm zou ook projecten met die tekst in naam of type tonen (Codex, 27 sep).
// Eerst op record-id (KlantIds: álle gekoppelde bedrijven), anders exact op de klantnaam
// (KlantnaamString is alleen de eerste klant; oudere engine zonder KlantIds).
export const normKlant = (v) => String(v || '').trim().toLowerCase();
export const matchtKlant = (p, klant, klantId = '') => {
  const k = normKlant(klant);
  if (!k && !klantId) return true;
  if (klantId && alsLijst(p.fields?.KlantIds).includes(klantId)) return true;
  return !!k && alsLijst(p.fields?.KlantnaamString).some((n) => normKlant(n) === k);
};

const segmentSx = (actief) => ({
  px: 1.25, py: 0.55, borderRadius: '0.45rem', cursor: 'pointer', userSelect: 'none',
  bgcolor: actief ? T.surface : 'transparent', boxShadow: actief ? '0 1px 2px rgba(45,51,53,0.12)' : 'none',
  display: 'flex', alignItems: 'center', gap: 0.5, whiteSpace: 'nowrap',
  fontFamily: T.fontBody, fontSize: '0.74rem', fontWeight: actief ? 600 : 400, color: actief ? T.text : T.outline,
  '&:hover': actief ? {} : { color: T.text },
});

const selectSx = {
  borderRadius: '0.55rem', fontSize: '0.74rem', fontFamily: T.fontBody, bgcolor: T.surface, height: 34,
  '& .MuiSelect-select': { py: 0.6, display: 'flex', alignItems: 'center', gap: 0.75 },
  '& fieldset': { borderColor: 'rgba(45,51,53,0.12)' },
};

/* ── Create Dialog ─────────────────────────────────────────────────────────── */
// Zoeken op elk stuk van de naam ("techn" vindt "Balm Speciale Technieken"), en
// niet meer dan 50 treffers tonen — bij ~2000 klanten is de lijst anders traag.
const keuzeFilter = createFilterOptions({
  matchFrom: 'any', trim: true, limit: 50,
  stringify: (o) => o?.label || '',
});

// Lege namen weg en dubbele labels samenvoegen: Airtable levert allebei, en een
// optie zonder label breekt het filteren van MUI (die leest option.label).
const schoneKeuzes = (rijen) => {
  const gezien = new Set();
  return rijen.filter((r) => {
    const label = String(r?.label || '').trim();
    if (!r?.id || !label || gezien.has(label.toLowerCase())) return false;
    gezien.add(label.toLowerCase());
    return true;
  });
};

function CreateProjectDialog({ open, onClose, onCreated }) {
  const [projectnaam, setProjectnaam] = useState('');
  const [status, setStatus] = useState('Lead');
  const [clients, setClients] = useState([]);
  const [personen, setPersonen] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedPL, setSelectedPL] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loadingData, setLoadingData] = useState(false);

  useEffect(() => {
    if (open) {
      setLoadingData(true);
      Promise.all([
        authFetch(`${AI_ENGINE_URL}/api/v1/airtable/clients?with_projects=false`).then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); }).catch(() => { toast.error('Klantenlijst niet geladen — sluit en open het venster opnieuw'); return { clients: [] }; }),
        authFetch(`${BACKEND_URL}/Api/personen`).then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); }).catch(() => { toast.error('Personenlijst niet geladen — projectleider kiezen lukt nu niet'); return { records: [] }; }),
      ]).then(([clientsData, personenData]) => {
        setClients(schoneKeuzes((clientsData.clients || []).map(c => ({ id: c.airtableId, label: c.name }))));
        setPersonen(schoneKeuzes((personenData.records || []).map(p => ({ id: p.id, label: p.fields?.['Volledige naam'] || p.fields?.Voornaam || '' }))));
        setLoadingData(false);
      });
    } else { setProjectnaam(''); setStatus('Lead'); setSelectedClient(null); setSelectedPL(null); }
  }, [open]);

  const handleCreate = async () => {
    if (!projectnaam.trim()) { toast.error('Projectnaam is verplicht'); return; }
    setSaving(true);
    try {
      const body = { projectnaam: projectnaam.trim(), status };
      if (selectedClient) body.klantnaam = [selectedClient.id];
      if (selectedPL) body.projectleider = [selectedPL.id];
      const resp = await authFetch(`${BACKEND_URL}/Api/projects`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json().catch(() => ({}));
      toast.success(`Project "${projectnaam}" aangemaakt`);
      // De lijst leest de spiegel; lukte het bijwerken daarvan niet, zeg dat dan
      // eerlijk in plaats van een project te tonen dat er nog niet in staat.
      if (data.mirrored === false) {
        toast('Staat in Airtable, maar de lijst loopt nog achter — ververs over een minuut.', { icon: '⏳' });
      }
      onClose();
      if (onCreated) onCreated({ plNaam: selectedPL?.label || '', status });
    } catch (err) { toast.error(`Fout bij aanmaken: ${err.message}`); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '0.75rem', p: 0.5 } }}>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
          <Typography sx={{ fontFamily: T.font, fontSize: '1.1rem', fontWeight: 800, color: T.text }}>Nieuw project</Typography>
          <Box onClick={onClose} sx={{ cursor: 'pointer', '&:hover': { opacity: 0.7 } }}><Icon name="close" size={20} sx={{ color: T.outline }} /></Box>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField label="Projectnaam" value={projectnaam} onChange={(e) => setProjectnaam(e.target.value)} required fullWidth autoFocus size="small" sx={inputSx} />
          <FormControl size="small" fullWidth sx={inputSx}>
            <Select value={status} onChange={(e) => setStatus(e.target.value)} sx={{ fontFamily: T.fontBody, fontSize: '0.82rem' }}>
              {['Lead', 'Offerte', 'In progress'].map(s => (
                <MenuItem key={s} value={s}>
                  <Typography component="span" sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', fontWeight: 600, color: (statusColorMap[s] || {}).text, bgcolor: (statusColorMap[s] || {}).bg, px: 1, py: 0.2, borderRadius: '9999px' }}>{s}</Typography>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Autocomplete options={clients} value={selectedClient} onChange={(_, val) => setSelectedClient(val)} loading={loadingData} renderInput={(params) => <TextField {...params} label="Klant" size="small" sx={inputSx} />} isOptionEqualToValue={(o, v) => o.id === v.id} size="small"
            getOptionLabel={(o) => o?.label || ''} filterOptions={keuzeFilter} autoHighlight noOptionsText="Geen klant gevonden" />
          <Autocomplete options={personen} value={selectedPL} onChange={(_, val) => setSelectedPL(val)} loading={loadingData} renderInput={(params) => <TextField {...params} label="Projectleider" size="small" sx={inputSx} />} isOptionEqualToValue={(o, v) => o.id === v.id} size="small"
            getOptionLabel={(o) => o?.label || ''} filterOptions={keuzeFilter} autoHighlight noOptionsText="Geen medewerker gevonden" />
        </Box>

        <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end', mt: 3 }}>
          <Box onClick={onClose} sx={{ px: 2, py: 0.75, borderRadius: '0.5rem', cursor: 'pointer', fontFamily: T.fontBody, fontWeight: 600, fontSize: '0.82rem', color: T.secondary, '&:hover': { bgcolor: T.surfaceLow } }}>
            Annuleren
          </Box>
          <Box
            onClick={!saving && projectnaam.trim() ? handleCreate : undefined}
            sx={{
              px: 2.5, py: 0.75, borderRadius: '0.5rem',
              cursor: !saving && projectnaam.trim() ? 'pointer' : 'default',
              background: !saving && projectnaam.trim() ? `linear-gradient(135deg, ${T.tertiary} 0%, ${T.primary} 100%)` : T.surfaceHigh,
              color: !saving && projectnaam.trim() ? '#fff' : T.outlineVariant,
              fontFamily: T.fontBody, fontWeight: 700, fontSize: '0.82rem',
              boxShadow: !saving && projectnaam.trim() ? '0 4px 12px rgba(99,89,131,0.25)' : 'none',
              display: 'flex', alignItems: 'center', gap: 0.5,
              transition: 'all 0.2s',
              '&:hover': !saving && projectnaam.trim() ? { boxShadow: '0 6px 20px rgba(99,89,131,0.35)' } : {},
            }}
          >
            <Icon name="add" size={16} sx={{ color: 'inherit' }} />
            {saving ? 'Aanmaken…' : 'Aanmaken'}
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
}

/* ── Skeleton ──────────────────────────────────────────────────────────────── */
function ProjectListSkeleton() {
  return (
    <Box sx={{ bgcolor: T.surface, borderRadius: '0.75rem', boxShadow: T.shadow, p: 1.5 }}>
      {[1, 2, 3, 4, 5, 6, 7].map(i => (
        <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.1, borderBottom: '1px solid rgba(45,51,53,0.05)' }}>
          <Skeleton variant="circular" width={10} height={10} />
          <Skeleton variant="text" width={40} height={18} />
          <Box sx={{ flex: 1.4 }}><Skeleton variant="text" width="70%" height={18} /><Skeleton variant="text" width="35%" height={14} /></Box>
          <Skeleton variant="rounded" width={80} height={18} sx={{ borderRadius: '9999px' }} />
          <Skeleton variant="circular" width={22} height={22} />
          <Box sx={{ flex: 1 }}><Skeleton variant="rounded" height={8} sx={{ borderRadius: 99 }} /></Box>
          <Skeleton variant="text" width={100} height={18} />
          <Skeleton variant="text" width={90} height={18} />
          <Skeleton variant="text" width={90} height={18} />
        </Box>
      ))}
    </Box>
  );
}

/* ── Main ──────────────────────────────────────────────────────────────────── */
function ProjectList() {
  const navigate = useNavigate();
  const { accounts } = useMsal();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [projects, setProjects] = useState([]);
  // ?klant=<naam> (vanuit een account: "Gekoppelde bedrijven") zoekt op die klant over
  // alle projectleiders; de rest van de onthouden filters blijft staan.
  const [searchParams, setSearchParams] = useSearchParams();
  const [beginFilters] = useState(leesFilters);
  const klantFilter = (searchParams.get('klant') || '').trim();
  const klantIdFilter = (searchParams.get('klantId') || '').trim();
  const wisKlantFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('klant');
    next.delete('klantId');
    setSearchParams(next, { replace: true });
  };
  const [searchTerm, setSearchTerm] = useState(beginFilters.zoek);
  const [serverHits, setServerHits] = useState([]);
  // Open Gripp-offertes zonder Airtable-record — draaien mee in Offertes & leads.
  const [grippOffers, setGrippOffers] = useState(null);
  const [leadBezig, setLeadBezig] = useState(null); // offertenummer dat wordt aangemaakt
  const zoekTimer = useRef(null);
  const zoekSeq = useRef(0);
  const [bereik, setBereik] = useState(beginFilters.bereik);
  const [statusFilter, setStatusFilter] = useState(beginFilters.status);
  const [sortering, setSortering] = useState(beginFilters.sortering);
  useEffect(() => {
    try {
      sessionStorage.setItem(FILTER_SLEUTEL, JSON.stringify({ bereik, status: statusFilter, sortering, zoek: searchTerm }));
    } catch { /* privémodus: dan niet onthouden */ }
  }, [bereik, statusFilter, sortering, searchTerm]);
  // Tabel ⇄ kaarten ⇄ mijn deadlines — tabel/kaarten-voorkeur onthouden.
  const [weergave, setWeergave] = useState(leesWeergave);
  const kiesWeergave = (w) => {
    setWeergave(w);
    if (w !== 'deadlines') { try { localStorage.setItem('projectenWeergave.v2', w); } catch { /* prima */ } }
  };
  // Extra kolommen (taken, urendetail, productie) — standaard dicht voor rust.
  const [details, setDetails] = useState(leesDetails);
  const kiesDetails = (aan) => {
    setDetails(aan);
    try { localStorage.setItem('projectenDetails.v1', aan ? '1' : '0'); } catch { /* prima */ }
  };
  const [stoplichten, setStoplichten] = useState({});
  const stoplichtBezig = useRef(new Set());
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  const includeArchived = statusFilter === 'archief' || statusFilter === 'alles';

  const userEmail = accounts?.[0]?.username || '';
  const userName = accounts?.[0]?.name || '';
  const userFirstName = userName.split(' ')[0]?.toLowerCase() || '';

  const loadProjects = async (withArchived = includeArchived) => {
    setLoading(true); setError(null);
    try {
      const url = withArchived ? `${BACKEND_URL}/Api/projects?includeArchived=true` : `${BACKEND_URL}/Api/projects`;
      const r = await authFetch(url);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setProjects(d.projects || d.records || []);
    } catch (err) { setError(`Kon projecten niet laden: ${err.message}`); }
    finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadProjects(includeArchived); }, [includeArchived]);

  // Open Gripp-offertes laden zodra leads of alles opengaat.
  useEffect(() => {
    if ((statusFilter !== 'leads' && statusFilter !== 'alles') || grippOffers !== null) return;
    authFetch(`${BACKEND_URL}/Api/gripp-offertes/open`)
      .then((r) => (r.ok ? r.json() : { offers: [] }))
      .then((d) => setGrippOffers(d.offers || []))
      .catch(() => setGrippOffers([]));
  }, [statusFilter, grippOffers]);

  // Server-zoek naast de client-filter: vindt draaidag-WERKNAMEN ("kadijk" →
  // 9677), leads/offertes buiten de geladen set én open Gripp-offertes die
  // (nog) geen project zijn. Best-effort: valt stil weg als de engine hapert.
  useEffect(() => {
    const q = searchTerm.trim();
    if (zoekTimer.current) clearTimeout(zoekTimer.current);
    const seq = ++zoekSeq.current; // trage oude response mag de actuele niet overschrijven
    if (q.length < 2) { setServerHits([]); return undefined; }
    zoekTimer.current = setTimeout(async () => {
      try {
        const r = await authFetch(`${BACKEND_URL}/Api/koppel-zoek?q=${encodeURIComponent(q)}`);
        if (r.ok && seq === zoekSeq.current) {
          const d = await r.json();
          if (seq === zoekSeq.current) setServerHits(d.opties || []);
        }
      } catch { /* zoeken is aanvullend, geen foutmelding waard */ }
    }, 350);
    return () => { if (zoekTimer.current) clearTimeout(zoekTimer.current); };
  }, [searchTerm]);

  const isUserProject = (project) => !!userFirstName
    && plVan(project).some(name => name.toLowerCase().includes(userFirstName));

  const matchtBereik = (p) => {
    if (bereik === BEREIK_ALLE) return true;
    if (bereik === BEREIK_MIJ) return isUserProject(p);
    return plVan(p).includes(bereik);
  };
  const matchtStatus = (p) => (
    statusFilter === 'lopend' ? isLopend(p)
      : statusFilter === 'leads' ? isLeadStatus(p)
        : statusFilter === 'archief' ? isAfgesloten(p)
          : true
  );

  const zoek = searchTerm.trim().toLowerCase();
  // Basis voor de tellers: bereik + zoekterm, nog zonder statusfilter
  // (test-feedback 20 aug: 'nummers veranderen niet bij zoeken').
  // Met een klantfilter telt het bereik niet: alle projecten van die klant.
  const heeftKlantFilter = !!(klantFilter || klantIdFilter);
  const basis = useMemo(() => projects.filter((p) => (heeftKlantFilter ? matchtKlant(p, klantFilter, klantIdFilter) : matchtBereik(p))
    && matchtZoek(p, zoek)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [projects, bereik, zoek, userFirstName, klantFilter, klantIdFilter]);
  const filteredProjects = useMemo(() => basis.filter(matchtStatus),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [basis, statusFilter]);

  const statusTellers = useMemo(() => ({
    lopend: basis.filter(isLopend).length,
    leads: basis.filter(isLeadStatus).length,
    archief: includeArchived ? basis.filter(isAfgesloten).length : null,
    alles: includeArchived ? basis.length : null,
  }), [basis, includeArchived]);

  const plOpties = useMemo(() => {
    const tel = new Map();
    projects.filter(isLopend).forEach((p) => plVan(p).forEach((n) => tel.set(n, (tel.get(n) || 0) + 1)));
    projects.forEach((p) => plVan(p).forEach((n) => { if (!tel.has(n)) tel.set(n, 0); }));
    return [...tel.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [projects]);
  const aantalMij = useMemo(() => projects.filter((p) => isLopend(p) && isUserProject(p)).length,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [projects, userFirstName]);
  const aantalAlle = useMemo(() => projects.filter(isLopend).length, [projects]);

  // Gezondheidsdata (Gripp + open taken/deliverables) voor de zichtbare lopende projecten.
  const lopendeIds = useMemo(() => filteredProjects.filter(isLopend).map((p) => p.id), [filteredProjects]);
  const {
    gripp, deadlines, planning, laden: gezondheidLaden, mislukt: gezondheidMislukt, grippNietBeschikbaar, opnieuw: gezondheidOpnieuw,
  } = useProjectGezondheid(lopendeIds);
  const aantalMislukt = useMemo(
    () => new Set([...gezondheidMislukt.deadlines, ...gezondheidMislukt.gripp]).size,
    [gezondheidMislukt],
  );

  const rijen = useMemo(() => {
    const vandaag = vandaagDag();
    return filteredProjects.map((p) => {
      const f = p.fields || {};
      const lead = isLeadStatus(p);
      const afgesloten = isAfgesloten(p);
      const gp = grippVoorProject(gripp, f);
      const items = deadlines ? deadlines[p.id] : undefined;
      const klaar = lead || afgesloten || (items !== undefined && !gezondheidLaden.gripp) || (!gezondheidLaden.deadlines && !gezondheidLaden.gripp);
      const gezondheid = klaar
        // items undefined = taken niet (of nog niet) opgehaald → 'onbekend', niet groen.
        ? berekenGezondheid({ gripp: gp, items: items || [], vandaag, isLead: lead, afgesloten, takenOnbekend: items === undefined })
        : null;
      const tpRaw = f.Projecttype || '';
      const type = Array.isArray(tpRaw) ? tpRaw.join(', ') : String(tpRaw || '');
      const pls = plVan(p);
      const team = [...new Set([...pls, ...alsLijst(f['Naam (from Creative)']), ...alsLijst(f['ROLLUP - Editors'])])];
      const rawTaken = f['Aantal taken'];
      return {
        id: p.id, project: p,
        nummer: f.Projectnummer ? String(f.Projectnummer) : '',
        naam: String(f.Projectnaam || f.Projectnr_en_naam || 'Project').trim(),
        klant: klantVanProject(f) || gp?.bedrijf || '',
        type: type && type !== 'Nvt' ? type : '',
        pls, team, isLead: lead, afgesloten,
        fase: lead || afgesloten ? f.Status : (gp?.fase || f.Status || ''),
        takenTotaal: Number(Array.isArray(rawTaken) ? rawTaken[0] : rawTaken) || 0,
        notities: Number(f['Aantal notities'] || 0) + Number(f['Aantal meetings'] || 0),
        gezondheid,
        planning: lead || afgesloten ? null : (planning?.[p.id] || null),
      };
    });
  }, [filteredProjects, gripp, deadlines, planning, gezondheidLaden]);

  const gesorteerd = useMemo(() => {
    const lijst = [...rijen];
    const nr = (r) => Number(r.nummer) || 0;
    const cmp = {
      risico: (a, b) => vergelijkRisico(a.gezondheid, b.gezondheid) || nr(b) - nr(a),
      deadline: (a, b) => (a.gezondheid?.volgende?.dagenTot ?? Infinity) - (b.gezondheid?.volgende?.dagenTot ?? Infinity) || nr(b) - nr(a),
      uren: (a, b) => (b.gezondheid?.uren?.fractie ?? -1) - (a.gezondheid?.uren?.fractie ?? -1),
      nummer: (a, b) => nr(b) - nr(a),
      naam: (a, b) => a.naam.localeCompare(b.naam),
      pl: (a, b) => (a.pls[0] || '~').localeCompare(b.pls[0] || '~') || vergelijkRisico(a.gezondheid, b.gezondheid),
    }[sortering] || (() => 0);
    return lijst.sort(cmp);
  }, [rijen, sortering]);

  const samenvatting = useMemo(() => {
    const tel = { te_laat: 0, risico: 0, op_schema: 0 };
    rijen.forEach((r) => { if (r.gezondheid && tel[r.gezondheid.status] !== undefined) tel[r.gezondheid.status] += 1; });
    return tel;
  }, [rijen]);

  // Productie-stoplichten (batch, gecached per sessie) voor zichtbare lopende projecten.
  useEffect(() => {
    // Alleen ophalen waar ze te zien zijn: kaarten, of de tabel met extra kolommen.
    if (weergave === 'deadlines' || (weergave === 'tabel' && !details) || !lopendeIds.length) return undefined;
    const timer = setTimeout(async () => {
      const nieuw = lopendeIds.filter((id) => stoplichten[id] === undefined && !stoplichtBezig.current.has(id));
      if (!nieuw.length) return;
      nieuw.forEach((id) => stoplichtBezig.current.add(id));
      const uit = await fetchProjectStatussen(nieuw);
      setStoplichten((prev) => ({ ...prev, ...uit }));
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weergave, details, lopendeIds]);

  // Zoekresultaten van de server die niet al als rij in beeld staan
  // (Gripp-offertes hebben sowieso geen rij; leads/werknaam-hits soms wel).
  const extraHits = useMemo(() => {
    // Met een klantfilter geen losse server-treffers: van die kennen we de klant niet zeker.
    if (searchTerm.trim().length < 2 || heeftKlantFilter) return [];
    const zichtbaar = new Set(filteredProjects.map(p => p.id));
    // Bewust GEEN filter op de server-treffers: wie zoekt wil vinden
    // (test 20 aug: het enige kadijk-resultaat viel weg op de leads-tab).
    return serverHits.filter(h => !zichtbaar.has(h.id));
  }, [serverHits, filteredProjects, searchTerm, heeftKlantFilter]);

  // Open Gripp-offerte als Airtable-lead aanmaken (26 aug): daarna draait hij
  // mee in de hele interne app. De kaart verhuist vanzelf naar de leads-lijst.
  const maakLead = async (e, o) => {
    e.stopPropagation();
    if (leadBezig) return;
    setLeadBezig(o.number);
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/gripp-offertes/${encodeURIComponent(o.number)}/maak-lead`, { method: 'POST' });
      const d = await r.json().catch(() => ({}));
      if (r.status === 409) { toast(`Offerte ${o.number} staat al in Airtable`); }
      else if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
      else {
        toast.success(`Offerte ${o.number} aangemaakt als lead${d.klantGekoppeld ? '' : ' (klant niet gevonden — koppel handmatig)'}`);
      }
      setGrippOffers((prev) => (prev || []).filter((x) => x.number !== o.number));
      loadProjects();
    } catch (err) {
      toast.error(`Lead aanmaken mislukt: ${err.message}`);
    } finally {
      setLeadBezig(null);
    }
  };

  // Gripp-offertes die nog géén Airtable-record hebben (dedupe op nummer),
  // gefilterd op de zoekterm. Ze hebben geen PL, dus het bereik geldt niet.
  const grippLijst = useMemo(() => {
    if (statusFilter !== 'leads' && statusFilter !== 'alles') return [];
    const bekend = new Set(projects.map((p) => String(p.fields?.Projectnummer || '')));
    return (grippOffers || []).filter((o) => !bekend.has(o.number))
      .filter((o) => !heeftKlantFilter || (!!klantFilter && normKlant(o.company) === normKlant(klantFilter)))
      .filter((o) => !zoek || `${o.number} ${o.subject} ${o.company}`.toLowerCase().includes(zoek));
  }, [grippOffers, projects, statusFilter, zoek, heeftKlantFilter, klantFilter]);

  // Gedeelde tijdas van de tabel: 6 weken terug t/m 10 weken vooruit.
  const venster = useMemo(() => {
    const v = vandaagDag();
    return { van: new Date(v.getFullYear(), v.getMonth(), v.getDate() - 42), tot: new Date(v.getFullYear(), v.getMonth(), v.getDate() + 70) };
  }, []);

  // Tweede argument = paneel op de projectpagina (bijv. 'budget' vanuit de urenkolom).
  // De voorvertoning (nummer/naam/klant) laat de projectpagina meteen de kop tonen en de
  // projectnummer-afhankelijke aanvragen tegelijk met de rest starten (geen waterval).
  const openProject = (r, paneel) => navigate(`/projects/${r.id}${paneel ? `?paneel=${paneel}` : ''}`, {
    state: { voorvertoning: { id: r.id, nummer: r.nummer || '', naam: r.naam || '', klant: r.klant || '' } },
  });

  const bereikNaam = bereik === BEREIK_MIJ ? 'jouw' : bereik === BEREIK_ALLE ? '' : `${bereik.split(' ')[0]}s`;
  const statusNaam = { lopend: 'lopende projecten', leads: 'offertes & leads', archief: 'afgesloten projecten', alles: 'projecten, offertes & leads' }[statusFilter];
  const subtitel = weergave === 'deadlines'
    ? 'Aankomende deadlines van jouw projecten'
    : loading ? 'Laden…'
      : `${filteredProjects.length} ${bereikNaam ? `${bereikNaam} ` : ''}${statusNaam}${statusFilter === 'lopend' || statusFilter === 'alles'
        ? ` · ${samenvatting.te_laat} te laat · ${samenvatting.risico} risico · ${samenvatting.op_schema} op schema` : ''}`;

  const zonderFilters = weergave === 'deadlines';

  return (
    <Box sx={{ width: '100%', maxWidth: 1800, mx: 'auto', px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 3, md: 5 }, pb: 10 }}>

      <PageHeader
        icon="folder_open"
        title="Projecten"
        subtitle={subtitel}
        rightActions={(
          <>
            <Box onClick={() => setShowCreateDialog(true)} sx={{
              px: 2, py: 0.75, borderRadius: '0.5rem', cursor: 'pointer',
              bgcolor: T.primary,
              color: '#fff', fontFamily: T.fontBody, fontWeight: 600, fontSize: '0.74rem',
              display: 'flex', alignItems: 'center', gap: 0.5,
              transition: 'background-color 0.15s',
              '&:hover': { bgcolor: T.primaryDim },
            }}>
              <Icon name="add" size={16} sx={{ color: '#fff' }} /> Nieuw project
            </Box>
            <Tooltip title="Vernieuwen" arrow slotProps={{ tooltip: { sx: { fontFamily: T.fontBody, fontSize: '0.72rem', bgcolor: T.text, borderRadius: '0.5rem' } }, arrow: { sx: { color: T.text, '&::before': { border: 'none' } } } }}>
              <Box onClick={() => loadProjects()} sx={{ width: 36, height: 36, borderRadius: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', '&:hover': { bgcolor: T.surfaceLow } }}>
                <Icon name="refresh" size={18} sx={{ color: loading ? T.outlineVariant : T.outline }} />
              </Box>
            </Tooltip>
          </>
        )}
      />

      {/* Bediening: bereik · status · zoeken · sorteren · weergave */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2, mt: -2.5, alignItems: 'center', flexWrap: 'wrap' }}>
        {!zonderFilters && (
          <>
            {heeftKlantFilter ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontFamily: T.fontBody, fontSize: '0.8rem', color: T.text }}>
                <Icon name="domain" size={16} sx={{ color: T.outline }} />
                Klant: <Box component="span" sx={{ fontWeight: 600 }}>{klantFilter || 'gekozen bedrijf'}</Box>
                <Box component="button" type="button" onClick={wisKlantFilter} aria-label="Klantfilter wissen"
                  title="Klantfilter wissen"
                  sx={{ border: 0, bgcolor: 'transparent', p: 0.25, cursor: 'pointer', color: T.outline, display: 'inline-flex', '&:hover': { color: T.text } }}>
                  <Icon name="close" size={16} />
                </Box>
              </Box>
            ) : (
            <FormControl size="small">
              <Select value={bereik} onChange={(e) => setBereik(e.target.value)} sx={{ ...selectSx, minWidth: 190 }}
                renderValue={(v) => (
                  <>
                    <Icon name={v === BEREIK_MIJ ? 'person' : v === BEREIK_ALLE ? 'groups' : 'badge'} size={16} sx={{ color: T.outline }} />
                    {v === BEREIK_MIJ ? 'Mijn projecten' : v === BEREIK_ALLE ? 'Alle projectleiders' : `PL: ${v}`}
                  </>
                )}>
                <MenuItem value={BEREIK_MIJ} sx={{ fontSize: '0.8rem' }}>Mijn projecten <Box component="span" sx={{ ml: 'auto', pl: 2, color: T.outline }}>{aantalMij}</Box></MenuItem>
                <MenuItem value={BEREIK_ALLE} sx={{ fontSize: '0.8rem' }}>Alle projectleiders <Box component="span" sx={{ ml: 'auto', pl: 2, color: T.outline }}>{aantalAlle}</Box></MenuItem>
                <ListSubheader sx={{ fontSize: '0.66rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', lineHeight: '28px', color: T.outline }}>Projectleider</ListSubheader>
                {/* Onthouden PL die (nog) niet in de lijst staat — anders klaagt Select */}
                {bereik !== BEREIK_MIJ && bereik !== BEREIK_ALLE && !plOpties.some(([n]) => n === bereik) && (
                  <MenuItem value={bereik} sx={{ display: 'none' }}>{bereik}</MenuItem>
                )}
                {plOpties.map(([n, aantal]) => (
                  <MenuItem key={n} value={n} sx={{ fontSize: '0.8rem' }}>{n}<Box component="span" sx={{ ml: 'auto', pl: 2, color: T.outline }}>{aantal}</Box></MenuItem>
                ))}
              </Select>
            </FormControl>
            )}

            <Box sx={{ display: 'flex', gap: 0.25, p: 0.35, bgcolor: T.surfaceHigh, borderRadius: '0.6rem' }}>
              {STATUS_OPTIES.map((o) => {
                const n = statusTellers[o.key];
                const extra = o.key === 'leads' && grippLijst.length ? ` + ${grippLijst.length} Gripp` : '';
                return (
                  <Box key={o.key} onClick={() => setStatusFilter(o.key)} sx={segmentSx(statusFilter === o.key)}>
                    {o.label}
                    {n != null && <Box component="span" sx={{ color: T.outline, fontWeight: 500 }}>{n}{extra}</Box>}
                  </Box>
                );
              })}
            </Box>

            <TextField
              placeholder="Zoek op project, klant, nummer of type…"
              value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><Icon name="search" size={18} sx={{ color: T.outlineVariant }} /></InputAdornment> }}
              sx={{ flex: 1, minWidth: 200, maxWidth: 380, ...inputSx, '& .MuiOutlinedInput-root': { ...inputSx['& .MuiOutlinedInput-root'], height: 34, bgcolor: T.surface } }}
            />

            <Box sx={{ flex: 1 }} />

            <FormControl size="small">
              <Select value={sortering} onChange={(e) => setSortering(e.target.value)} sx={{ ...selectSx, minWidth: 150 }}
                renderValue={(v) => (<><Icon name="sort" size={16} sx={{ color: T.outline }} />Sorteer: {(SORTERINGEN.find(([k]) => k === v) || [])[2]}</>)}>
                {SORTERINGEN.map(([k, l]) => <MenuItem key={k} value={k} sx={{ fontSize: '0.8rem' }}>{l}</MenuItem>)}
              </Select>
            </FormControl>
          </>
        )}
        {zonderFilters && <Box sx={{ flex: 1 }} />}

        <Box sx={{ display: 'flex', gap: 0.25, p: 0.35, bgcolor: T.surfaceHigh, borderRadius: '0.6rem' }}>
          {[['tabel', 'table_rows', 'Tabel'], ['kaarten', 'grid_view', 'Kaarten'], ['deadlines', 'event', 'Mijn deadlines']].map(([w, icoon, label]) => (
            <Box key={w} onClick={() => kiesWeergave(w)} sx={segmentSx(weergave === w)}>
              <Icon name={icoon} size={15} sx={{ color: T.outline }} />{label}
            </Box>
          ))}
        </Box>
      </Box>

      {/* Mijn deadlines */}
      {weergave === 'deadlines' && <DeadlineOverview userEmail={userEmail} projecten={projects} />}

      {/* Projecten */}
      {weergave !== 'deadlines' && (
        <>
          {error && (
            <Box sx={{ p: 3, textAlign: 'center', bgcolor: T.errorBg, borderRadius: '0.75rem', border: `1px solid ${T.error}20` }}>
              <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.82rem', color: T.error, mb: 1 }}>{error}</Typography>
              <Typography onClick={() => loadProjects()} sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', color: T.primary, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
                Opnieuw proberen
              </Typography>
            </Box>
          )}

          {loading && !error && <ProjectListSkeleton />}

          {!loading && !error && filteredProjects.length === 0 && extraHits.length === 0 && grippLijst.length === 0 && (
            <Box sx={{ py: 8, textAlign: 'center' }}>
              <Icon name="folder_open" size={48} sx={{ color: T.outlineVariant, display: 'block', margin: '0 auto 12px' }} />
              <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.92rem', fontWeight: 600, color: T.outline, mb: 0.5 }}>Geen projecten gevonden</Typography>
              <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', color: T.outlineVariant }}>
                {searchTerm ? 'Pas je zoekopdracht aan' : bereik === BEREIK_MIJ ? 'Kies "Alle projectleiders" om de rest te zien' : 'Er zijn hier geen projecten'}
              </Typography>
            </Box>
          )}

          {!loading && !error && filteredProjects.length > 0 && (
            weergave === 'tabel' ? (
              <ProjectTabel rijen={gesorteerd} venster={venster} sortering={sortering} onSorteer={setSortering}
                onOpen={openProject} stoplichten={stoplichten} details={details} onDetails={kiesDetails} />
            ) : (
              <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: 'repeat(auto-fill, minmax(330px, 1fr))' }}>
                {gesorteerd.map((r) => (
                  <ProjectKaart key={r.id} rij={r} stoplicht={stoplichten[r.id]} onOpen={openProject} />
                ))}
              </Box>
            )
          )}

          {!loading && !error && filteredProjects.length > 0 && (gezondheidLaden.gripp || gezondheidLaden.deadlines) && (
            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.68rem', color: T.outlineVariant, mt: 1 }}>
              Uren en taken worden geladen…
            </Typography>
          )}
          {!loading && !error && !gezondheidLaden.gripp && !gripp && statusFilter !== 'leads' && filteredProjects.length > 0 && (
            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.68rem', color: T.outline, mt: 1 }}>
              {grippNietBeschikbaar
                ? 'Fase en uren uit Gripp zijn hier nog niet beschikbaar; het signaal kijkt alleen naar taken en deadlines.'
                : 'Gripp-gegevens niet bereikbaar — fase en uren ontbreken tijdelijk; het signaal kijkt dan alleen naar taken en deadlines.'}
            </Typography>
          )}
          {!loading && !error && !gezondheidLaden.gripp && !gezondheidLaden.deadlines && aantalMislukt > 0 && (
            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.68rem', color: T.outline, mt: 1 }}>
              Voor {aantalMislukt} {aantalMislukt === 1 ? 'project' : 'projecten'} konden taken of uren niet worden opgehaald.{' '}
              <Box component="button" type="button" onClick={gezondheidOpnieuw}
                sx={{ border: 0, background: 'none', p: 0, font: 'inherit', color: T.primary, cursor: 'pointer', textDecoration: 'underline' }}>
                Opnieuw proberen
              </Box>
            </Typography>
          )}

          {/* Open Gripp-offertes zonder Airtable-record — horen thuis in het
              offerte-overzicht ook al bestaat er (nog) geen lead-record. */}
          {!loading && !error && grippLijst.length > 0 && (
            <Box sx={{ mt: filteredProjects.length > 0 ? 4 : 0 }}>
              <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', fontWeight: 700, color: T.outline, textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1.5 }}>
                Open offertes in Gripp — nog geen project of lead in Airtable ({grippLijst.length})
              </Typography>
              <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '1fr 1fr 1fr' } }}>
                {grippLijst.map((o) => (
                  <Box key={o.number} onClick={() => navigate(`/offertes?mode=new&number=${o.number}`)} sx={{
                    p: 2, borderRadius: '0.75rem', bgcolor: T.surface, border: `1px solid ${T.outlineVariant}30`,
                    cursor: 'pointer', transition: 'all 0.15s',
                    '&:hover': { bgcolor: T.surfaceLow, borderColor: `${T.primary}40` },
                  }}>
                    <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.85rem', fontWeight: 700, color: T.text }}>
                      {o.number}. {o.subject}
                    </Typography>
                    <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.outline, display: 'block', mt: 0.25 }}>
                      {[o.company, o.phase].filter(Boolean).join(' · ')}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.5 }}>
                      <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.tertiary, fontWeight: 700 }}>
                        Gripp-offerte — klik voor de generator
                      </Typography>
                      <Typography component="span" role="button" variant="caption"
                        onClick={(e) => maakLead(e, o)}
                        sx={{ fontFamily: T.fontBody, fontWeight: 700, cursor: 'pointer',
                          color: leadBezig === o.number ? T.outlineVariant : T.primary,
                          px: 1, py: 0.25, borderRadius: '9999px', border: `1px solid ${T.primary}40`,
                          '&:hover': { bgcolor: `${T.primary}10` } }}>
                        {leadBezig === o.number ? 'Bezig…' : '➕ Lead in Airtable'}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {/* Server-zoek: hits buiten de rijen hierboven — leads/offertes
              buiten de geladen set, Gripp-offertes zonder project, en projecten
              die alleen op hun draaidag-WERKNAAM matchen. */}
          {!loading && !error && extraHits.length > 0 && (
            <Box sx={{ mt: filteredProjects.length > 0 ? 4 : 0 }}>
              <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', fontWeight: 700, color: T.outline, textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1.5 }}>
                Ook gevonden — {bereik === BEREIK_MIJ ? 'álle projecten, offertes & leads (server-zoek kent geen eigenaar)' : 'offertes, leads & werknamen'}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxWidth: 720 }}>
                {extraHits.map(h => {
                  const offerteNr = h.type === 'offerte' && /^offer-(\d+)$/.test(h.id) ? h.id.slice(6) : null;
                  const klikbaar = h.type !== 'offerte' || !!offerteNr;
                  const typeLabel = h.type === 'offerte' ? 'Gripp-offerte → generator' : h.type === 'lead' ? 'Lead / offerte' : 'Project';
                  const typeIcon = h.type === 'offerte' ? 'request_quote' : h.type === 'lead' ? 'trending_up' : 'folder_open';
                  const doel = offerteNr ? `/offertes?mode=new&number=${offerteNr}` : projectHref(h.id);
                  return (
                    <Box key={h.id} {...(klikbaar ? { component: 'a', href: doel } : {})}
                      onClick={klikbaar ? (e) => opGewoneKlik(e, () => navigate(doel)) : undefined} sx={{
                      color: 'inherit', textDecoration: 'none',
                      display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.25,
                      borderRadius: '0.75rem', bgcolor: T.surface, border: `1px solid ${T.outlineVariant}30`,
                      cursor: klikbaar ? 'pointer' : 'default',
                      transition: 'all 0.15s',
                      ...(klikbaar ? { '&:hover': { bgcolor: T.surfaceLow, borderColor: `${T.primary}40` } } : {}),
                    }}>
                      <Icon name={typeIcon} size={18} sx={{ color: T.outline, flexShrink: 0 }} />
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.82rem', fontWeight: 600, color: T.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {h.label}
                        </Typography>
                        {h.sub && (
                          <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', color: T.outline, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {h.sub}
                          </Typography>
                        )}
                      </Box>
                      <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.66rem', fontWeight: 700, color: h.type === 'offerte' ? T.tertiary : T.primary, bgcolor: h.type === 'offerte' ? `${T.tertiary}14` : T.primaryContainer, px: 1, py: 0.25, borderRadius: '9999px', flexShrink: 0 }}>
                        {typeLabel}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}
        </>
      )}

      {/* Een net aangemaakt project van een ándere projectleider valt buiten
          "Mijn projecten" — dan overschakelen naar alle projectleiders, anders
          lijkt het aanmaken mislukt. Nieuwe projecten starten vaak als lead. */}
      <CreateProjectDialog open={showCreateDialog} onClose={() => setShowCreateDialog(false)}
        onCreated={({ plNaam, status } = {}) => {
          const vanMij = userFirstName && String(plNaam || '').toLowerCase().includes(userFirstName);
          if (bereik === BEREIK_MIJ && !vanMij) setBereik(BEREIK_ALLE);
          if (LEAD_STATUSSEN.includes(status) && statusFilter === 'lopend') setStatusFilter('leads');
          loadProjects();
        }} />
    </Box>
  );
}

export default ProjectList;
